import type { Provider } from '@angular/core';
import { EVENT_RENDERER_REGISTRY, type EventRendererEntry } from './event-renderer';
import {
  ConversationArchivedRendererComponent,
  ConversationChannelUnavailableRendererComponent,
  ConversationCreatedRendererComponent,
  ConversationLabelAddedRendererComponent,
  ConversationLabelRemovedRendererComponent,
  ConversationPriorityChangedRendererComponent,
  ConversationReopenedRendererComponent,
  ConversationResolvedRendererComponent,
  ConversationSnoozedRendererComponent,
  ConversationStatusChangedRendererComponent,
  FallbackRendererComponent,
  SessionAssignedRendererComponent,
  SessionClosedRendererComponent,
  SessionOpenedRendererComponent,
} from './renderers';

const ENTRIES: readonly EventRendererEntry[] = [
  { eventType: 'conversation.created', component: ConversationCreatedRendererComponent },
  { eventType: 'conversation.status_changed', component: ConversationStatusChangedRendererComponent },
  { eventType: 'conversation.priority_changed', component: ConversationPriorityChangedRendererComponent },
  { eventType: 'conversation.snoozed', component: ConversationSnoozedRendererComponent },
  { eventType: 'conversation.resolved', component: ConversationResolvedRendererComponent },
  { eventType: 'conversation.archived', component: ConversationArchivedRendererComponent },
  { eventType: 'conversation.reopened', component: ConversationReopenedRendererComponent },
  { eventType: 'conversation.channel_unavailable', component: ConversationChannelUnavailableRendererComponent },
  { eventType: 'conversation.label_added', component: ConversationLabelAddedRendererComponent },
  { eventType: 'conversation.label_removed', component: ConversationLabelRemovedRendererComponent },
  { eventType: 'session.opened', component: SessionOpenedRendererComponent },
  { eventType: 'session.closed', component: SessionClosedRendererComponent },
  { eventType: 'session.assigned', component: SessionAssignedRendererComponent },
  { eventType: '*', component: FallbackRendererComponent },
];

/**
 * Canonical list of eventTypes shown in the conversation history. Operational view —
 * handoffs, priorities, SLA, lifecycle, labels — not the message thread itself.
 */
export const HISTORY_VISIBLE_EVENT_TYPES: readonly string[] = ENTRIES
  .map((entry) => entry.eventType)
  .filter((eventType) => eventType !== '*');

/**
 * Providers that expose the timeline renderer registry. Drop into a route or component's
 * `providers: [...]` to make {@link EVENT_RENDERER_REGISTRY} resolvable downstream.
 *
 * Add a new event type in three steps:
 *   1. Backend emits `eventType: 'foo.bar'`.
 *   2. Write a standalone @Component in `renderers/index.ts` (typed payload, uses
 *      `<app-history-row>`).
 *   3. Append `{ eventType: 'foo.bar', component: FooBarRenderer }` to {@link ENTRIES}.
 */
export function provideEventRendererRegistry(): Provider[] {
  return [{ provide: EVENT_RENDERER_REGISTRY, useValue: ENTRIES }];
}
